import { useEffect, useRef, useState } from "react";
import {
  bearingDegrees,
  offsetByHeadingSpeed,
  type LatLng,
} from "shared/lib/tracking/geo";
import { easeOutCubic, lerpLatLng } from "shared/lib/tracking/interpolate";
import type { DriverLocationSnapshot } from "types/tracking.types";

const DEFAULT_ANIMATION_MS = 3000;
const PREDICT_AFTER_MS = 2000;
const MAX_PREDICT_MS = 10_000;

export interface AnimatedPositionResult {
  position: LatLng | null;
  bearing: number;
  isStale: boolean;
  lastUpdated: string | null;
}

export interface UseAnimatedPositionOptions {
  /** Shorter = snappier mock movement (e.g. 500). */
  animationMs?: number;
  enablePrediction?: boolean;
}

export function useAnimatedPosition(
  snapshot: DriverLocationSnapshot | null | undefined,
  options?: UseAnimatedPositionOptions,
): AnimatedPositionResult {
  const animationMs = options?.animationMs ?? DEFAULT_ANIMATION_MS;
  const enablePrediction = options?.enablePrediction ?? true;
  const [display, setDisplay] = useState<LatLng | null>(null);
  const [bearing, setBearing] = useState(0);
  const [isStale, setIsStale] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const displayRef = useRef<LatLng | null>(null);
  const targetRef = useRef<LatLng | null>(null);
  const animStartRef = useRef<number | null>(null);
  const animFromRef = useRef<LatLng | null>(null);
  const rafRef = useRef<number | null>(null);
  const predictMetaRef = useRef<{
    heading?: number;
    speed?: number;
    updatedAt: string;
  } | null>(null);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    if (!snapshot) {
      setDisplay(null);
      setLastUpdated(null);
      setIsStale(false);
      targetRef.current = null;
      return;
    }

    const target: LatLng = { lat: snapshot.lat, lng: snapshot.lng };
    targetRef.current = target;
    predictMetaRef.current = {
      heading: snapshot.heading,
      speed: snapshot.speed,
      updatedAt: snapshot.updatedAt,
    };
    setLastUpdated(snapshot.updatedAt);

    const from = displayRef.current ?? target;
    animFromRef.current = from;
    animStartRef.current = performance.now();

    if (snapshot.heading != null) {
      setBearing(snapshot.heading);
    } else if (displayRef.current) {
      setBearing(bearingDegrees(displayRef.current, target));
    }
  }, [snapshot]);

  useEffect(() => {
    const tick = (now: number) => {
      const target = targetRef.current;
      const meta = predictMetaRef.current;

      if (!target) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const updatedMs = meta ? new Date(meta.updatedAt).getTime() : now;
      const ageMs = now - updatedMs;
      setIsStale(ageMs > 30_000);

      const animStart = animStartRef.current;
      const animFrom = animFromRef.current;

      if (animStart != null && animFrom) {
        const elapsed = now - animStart;
        const t = easeOutCubic(elapsed / animationMs);
        if (elapsed < animationMs) {
          const pos = lerpLatLng(animFrom, target, t);
          displayRef.current = pos;
          setDisplay(pos);
          rafRef.current = requestAnimationFrame(tick);
          return;
        }
        animStartRef.current = null;
        displayRef.current = target;
        setDisplay(target);
      }

      if (
        enablePrediction &&
        ageMs > PREDICT_AFTER_MS &&
        meta?.heading != null &&
        meta?.speed != null &&
        meta.speed > 0 &&
        ageMs < MAX_PREDICT_MS
      ) {
        const dt = Math.min(1 / 60, (ageMs - PREDICT_AFTER_MS) / 1000);
        const base = displayRef.current ?? target;
        const predicted = offsetByHeadingSpeed(
          base,
          meta.heading,
          meta.speed,
          dt,
        );
        displayRef.current = predicted;
        setDisplay(predicted);
        setBearing(meta.heading);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [animationMs, enablePrediction]);

  return { position: display, bearing, isStale, lastUpdated };
}
